// Cache bust v2 - Feb 21 2026
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  ArrowLeft,
  Plus,
  Zap,
  Clock,
  Users,
  CheckCircle2,
  Circle,
  MessageSquare,
  Trash2,
  Edit,
  Send,
  UserPlus,
  X,
  ChevronDown,
  Lightbulb,
  ArrowRight,
  Image,
  Upload,
  CalendarIcon,
  AlertTriangle,
  GripVertical
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showTaskDetail, setShowTaskDetail] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  
  // Form states
  const [newTask, setNewTask] = useState({ title: "", description: "", priority: 5, assigned_to: "", images: [], due_date: null });
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberName, setNewMemberName] = useState("");
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [newIdea, setNewIdea] = useState("");
  const [newIdeaImages, setNewIdeaImages] = useState([]);
  const [activeTab, setActiveTab] = useState("tasks"); // "tasks" or "ideas"
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editingDescription, setEditingDescription] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const [editingProjectDescription, setEditingProjectDescription] = useState(false);
  const [newProjectDescription, setNewProjectDescription] = useState("");

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, tasksRes, membersRes, ideasRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${API}/projects/${projectId}/tasks`, { withCredentials: true }),
        axios.get(`${API}/projects/${projectId}/members`, { withCredentials: true }),
        axios.get(`${API}/projects/${projectId}/ideas`, { withCredentials: true })
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setMembers(membersRes.data);
      setIdeas(ideasRes.data);
    } catch (error) {
      console.error("Failed to fetch project:", error);
      toast.error("Failed to load project");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) {
      toast.error("Task title required");
      return;
    }

    setSubmitting(true);
    try {
      const taskData = {
        ...newTask,
        due_date: newTask.due_date ? newTask.due_date.toISOString().split('T')[0] : null
      };
      const response = await axios.post(`${API}/projects/${projectId}/tasks`, taskData, { withCredentials: true });
      setTasks([response.data, ...tasks]);
      setShowTaskModal(false);
      setNewTask({ title: "", description: "", priority: 5, assigned_to: "", images: [], due_date: null });
      toast.success("Task created successfully");
    } catch (error) {
      toast.error("Failed to create task");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const response = await axios.put(`${API}/tasks/${taskId}`, updates, { withCredentials: true });
      
      // If priority changed, refetch all tasks to get swapped priorities
      if (updates.priority !== undefined) {
        const tasksRes = await axios.get(`${API}/projects/${projectId}/tasks`, { withCredentials: true });
        setTasks(tasksRes.data);
        if (showTaskDetail?.task_id === taskId) {
          const updatedTask = tasksRes.data.find(t => t.task_id === taskId);
          if (updatedTask) setShowTaskDetail(updatedTask);
        }
      } else {
        setTasks(tasks.map(t => t.task_id === taskId ? response.data : t));
        if (showTaskDetail?.task_id === taskId) {
          setShowTaskDetail(response.data);
        }
      }
      toast.success("Task updated");
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;
    
    // Get the current sorted tasks (only pending ones for drag)
    const pendingTasks = [...sortedTasks].filter(t => t.status !== "completed");
    const completedTasks = tasks.filter(t => t.status === "completed");
    
    // Reorder the array
    const [movedTask] = pendingTasks.splice(sourceIndex, 1);
    pendingTasks.splice(destIndex, 0, movedTask);
    
    // Calculate new priorities (1-based, lower = higher priority)
    const updates = pendingTasks.map((task, index) => ({
      task_id: task.task_id,
      new_priority: index + 1
    }));
    
    // Optimistically update the UI
    const updatedTasks = pendingTasks.map((task, index) => ({
      ...task,
      priority: index + 1
    }));
    setTasks([...updatedTasks, ...completedTasks]);
    
    // Update priorities in backend
    try {
      for (const update of updates) {
        if (update.new_priority !== tasks.find(t => t.task_id === update.task_id)?.priority) {
          await axios.put(`${API}/tasks/${update.task_id}`, { priority: update.new_priority }, { withCredentials: true });
        }
      }
      // Refetch to ensure consistency
      const tasksRes = await axios.get(`${API}/projects/${projectId}/tasks`, { withCredentials: true });
      setTasks(tasksRes.data);
      toast.success("Task order updated");
    } catch (error) {
      // Revert on error
      const tasksRes = await axios.get(`${API}/projects/${projectId}/tasks`, { withCredentials: true });
      setTasks(tasksRes.data);
      toast.error("Failed to update task order");
    }
  };

  const handleUpdateDescription = async () => {
    if (!showTaskDetail) return;
    
    try {
      const response = await axios.put(
        `${API}/tasks/${showTaskDetail.task_id}`,
        { description: newDescription },
        { withCredentials: true }
      );
      setTasks(tasks.map(t => t.task_id === showTaskDetail.task_id ? response.data : t));
      setShowTaskDetail(response.data);
      setEditingDescription(false);
      toast.success("Description updated");
    } catch (error) {
      toast.error("Failed to update description");
    }
  };

  const handleUpdateProjectDescription = async () => {
    try {
      const response = await axios.put(
        `${API}/projects/${projectId}`,
        { description: newProjectDescription },
        { withCredentials: true }
      );
      setProject(response.data);
      setEditingProjectDescription(false);
      toast.success("Project description updated");
    } catch (error) {
      toast.error("Failed to update project description");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      setTasks(tasks.filter(t => t.task_id !== taskId));
      setShowTaskDetail(null);
      toast.success("Task deleted");
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMemberName.trim()) {
      toast.error("Name required");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/members`, 
        { name: newMemberName, email: newMemberEmail || null }, 
        { withCredentials: true }
      );
      setMembers([...members, response.data.member]);
      setShowMemberModal(false);
      setNewMemberName("");
      setNewMemberEmail("");
      toast.success("Team member added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    if (!editingMember || !editingMember.name.trim()) {
      toast.error("Name required");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.put(
        `${API}/projects/${projectId}/members/${editingMember.user_id}`,
        { name: editingMember.name },
        { withCredentials: true }
      );
      setMembers(members.map(m => m.user_id === editingMember.user_id ? response.data.member : m));
      setEditingMember(null);
      toast.success("Member updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update member");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    const memberToDelete = members.find(m => m.user_id === memberId);
    if (!window.confirm(`Remove ${memberToDelete?.name} from this project?`)) return;

    try {
      await axios.delete(`${API}/projects/${projectId}/members/${memberId}`, { withCredentials: true });
      setMembers(members.filter(m => m.user_id !== memberId));
      // Refresh tasks to update any that were assigned to this member
      const tasksRes = await axios.get(`${API}/projects/${projectId}/tasks`, { withCredentials: true });
      setTasks(tasksRes.data);
      toast.success("Member removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const openTaskDetail = async (task) => {
    setShowTaskDetail(task);
    try {
      const response = await axios.get(`${API}/tasks/${task.task_id}/comments`, { withCredentials: true });
      setComments(response.data);
    } catch (error) {
      console.error("Failed to load comments");
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const response = await axios.post(
        `${API}/tasks/${showTaskDetail.task_id}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      setComments([...comments, response.data]);
      setNewComment("");
      toast.success("Comment added");
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const handleAddIdea = async (e) => {
    e.preventDefault();
    if (!newIdea.trim()) return;

    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/ideas`,
        { content: newIdea, images: newIdeaImages },
        { withCredentials: true }
      );
      setIdeas([response.data, ...ideas]);
      setNewIdea("");
      setNewIdeaImages([]);
      toast.success("Idea added");
    } catch (error) {
      toast.error("Failed to add idea");
    }
  };

  const handleDeleteIdea = async (ideaId) => {
    try {
      await axios.delete(`${API}/ideas/${ideaId}`, { withCredentials: true });
      setIdeas(ideas.filter(i => i.idea_id !== ideaId));
      toast.success("Idea removed");
    } catch (error) {
      toast.error("Failed to delete idea");
    }
  };

  const handleUpdateIdea = async (e) => {
    e.preventDefault();
    if (!editingIdea) return;
    
    setSubmitting(true);
    try {
      const response = await axios.put(
        `${API}/ideas/${editingIdea.idea_id}`,
        { content: editingIdea.content, images: editingIdea.images || [] },
        { withCredentials: true }
      );
      setIdeas(ideas.map(i => i.idea_id === editingIdea.idea_id ? response.data : i));
      setEditingIdea(null);
      toast.success("Idea updated");
    } catch (error) {
      toast.error("Failed to update idea");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditIdeaImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setEditingIdea({
        ...editingIdea,
        images: [...(editingIdea.images || []), response.data.url]
      });
      toast.success("Image uploaded");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const removeEditIdeaImage = (index) => {
    setEditingIdea({
      ...editingIdea,
      images: editingIdea.images.filter((_, i) => i !== index)
    });
  };

  const handleConvertIdea = async (ideaId) => {
    try {
      const response = await axios.post(`${API}/ideas/${ideaId}/convert`, {}, { withCredentials: true });
      setIdeas(ideas.filter(i => i.idea_id !== ideaId));
      setTasks([response.data.task, ...tasks]);
      toast.success("Idea converted to task");
    } catch (error) {
      toast.error("Failed to convert idea");
    }
  };

  const handleConvertTaskToIdea = async (taskId) => {
    try {
      const response = await axios.post(`${API}/tasks/${taskId}/convert-to-idea`, {}, { withCredentials: true });
      setTasks(tasks.filter(t => t.task_id !== taskId));
      setIdeas([response.data.idea, ...ideas]);
      setShowTaskDetail(null);
      toast.success("Task moved back to ideas");
    } catch (error) {
      toast.error("Failed to convert task to idea");
    }
  };

  const handleImageUpload = async (e, target) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (target === 'task') {
        setNewTask({ ...newTask, images: [...newTask.images, response.data.url] });
      } else if (target === 'idea') {
        setNewIdeaImages([...newIdeaImages, response.data.url]);
      } else if (target === 'taskDetail' && showTaskDetail) {
        const updatedImages = [...(showTaskDetail.images || []), response.data.url];
        await handleUpdateTask(showTaskDetail.task_id, { images: updatedImages });
      }
      toast.success("Image uploaded");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const removeImage = (target, index) => {
    if (target === 'task') {
      setNewTask({ ...newTask, images: newTask.images.filter((_, i) => i !== index) });
    } else if (target === 'idea') {
      setNewIdeaImages(newIdeaImages.filter((_, i) => i !== index));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDueDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === "completed") return false;
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
  };

  const isDueSoon = (dueDate, status) => {
    if (!dueDate || status === "completed") return false;
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const threeDaysFromNow = new Date(today);
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
    return due >= today && due <= threeDaysFromNow;
  };

  const getMemberName = (userId) => {
    const member = members.find(m => m.user_id === userId);
    return member?.name || "Unassigned";
  };

  const getMemberById = (userId) => {
    return members.find(m => m.user_id === userId);
  };

  const getPriorityColor = () => {
    // No color coding - use neutral styling
    return "priority-neutral";
  };

  const getPriorityLabel = () => {
    // No color coding - use neutral color
    return "text-arc-cyan";
  };

  const getPriorityText = (priority) => {
    // Just return the number
    const p = parseInt(priority);
    return isNaN(p) ? "" : `#${p}`;
  };

  // Sort tasks by priority number (1 first, then 2, etc.) - completed at bottom
  const sortedTasks = [...tasks].sort((a, b) => {
    // Completed at bottom
    if (a.status === "completed" && b.status !== "completed") return 1;
    if (a.status !== "completed" && b.status === "completed") return -1;
    
    // By priority number ascending (1, 2, 3... 10)
    const aPriority = typeof a.priority === 'number' ? a.priority : parseInt(a.priority) || 10;
    const bPriority = typeof b.priority === 'number' ? b.priority : parseInt(b.priority) || 10;
    return aPriority - bPriority;
  });

  // Get used priorities for showing in dropdowns
  const usedPriorities = tasks
    .filter(t => t.status !== "completed")
    .map(t => typeof t.priority === 'number' ? t.priority : parseInt(t.priority))
    .filter(p => !isNaN(p));

  if (loading) {
    return (
      <div className="min-h-screen bg-stark-void flex items-center justify-center">
        <div className="text-center">
          <div className="arc-loader mx-auto mb-6"></div>
          <p className="font-display text-arc-cyan uppercase tracking-widest text-sm">
            Loading project...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stark-void">
      {/* Background Grid */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 240, 255, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 240, 255, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Header */}
      <header className="glass border-b border-arc-cyan/20 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard"
              className="p-2 border border-arc-cyan/30 text-arc-cyan hover:bg-arc-cyan/10 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-display text-lg text-arc-cyan uppercase tracking-widest">
                {project?.name}
              </h1>
              <p className="font-mono text-xs text-arc-cyan/50">PROJECT // {projectId.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowMemberModal(true)}
              data-testid="add-member-btn"
              className="flex items-center gap-2 px-3 py-2 border border-arc-cyan/30 text-arc-cyan text-xs font-mono uppercase tracking-wider hover:bg-arc-cyan/10 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Member</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 relative z-10">
        {/* Project Info */}
        <div className="glass border border-arc-cyan/20 p-6 mb-8">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex-1 min-w-[200px]">
              {editingProjectDescription ? (
                <div className="space-y-2">
                  <Textarea
                    value={newProjectDescription}
                    onChange={(e) => setNewProjectDescription(e.target.value)}
                    placeholder="Add a project description..."
                    data-testid="edit-project-description-input"
                    className="bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan min-h-[60px]"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleUpdateProjectDescription}
                      data-testid="save-project-description-btn"
                      className="bg-arc-cyan/10 border border-arc-cyan text-arc-cyan hover:bg-arc-cyan hover:text-stark-void text-xs"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingProjectDescription(false)}
                      className="text-muted-foreground hover:text-holo-white text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 group">
                  <p className="text-muted-foreground flex-1">
                    {project?.description || <span className="italic text-muted-foreground/50">No project description</span>}
                  </p>
                  <button
                    onClick={() => {
                      setNewProjectDescription(project?.description || "");
                      setEditingProjectDescription(true);
                    }}
                    className="p-1 text-arc-cyan/50 hover:text-arc-cyan transition-colors opacity-0 group-hover:opacity-100"
                    data-testid="edit-project-description-btn"
                    title="Edit project description"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm font-mono">
              <div className="flex items-center gap-2 text-arc-cyan/70">
                <Users className="w-4 h-4" />
                <span>{members.length} member{members.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-2 text-arc-cyan/70">
                <CheckCircle2 className="w-4 h-4" />
                <span>{tasks.filter(t => t.status === "completed").length}/{tasks.length} completed</span>
              </div>
            </div>
          </div>
          
          {/* Team Members */}
          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-arc-cyan/10 flex-wrap">
            <span className="text-xs font-mono text-arc-cyan/50 uppercase tracking-wider">Team:</span>
            {members.map((member) => (
              <div key={member.user_id} className="flex items-center gap-2 glass px-2 py-1 border border-arc-cyan/20 group">
                <Avatar className="w-6 h-6 border border-arc-cyan/30">
                  <AvatarImage src={member.picture} />
                  <AvatarFallback className="bg-arc-cyan/20 text-arc-cyan text-xs font-display">
                    {member.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-holo-white font-mono">{member.name}</span>
                {project?.owner_id === user?.user_id && member.user_id !== project?.owner_id && (
                  <div className="flex items-center gap-1 ml-1">
                    <button
                      onClick={() => setEditingMember({ ...member })}
                      data-testid={`edit-member-${member.user_id}`}
                      className="p-1 text-arc-cyan/50 hover:text-arc-cyan transition-colors"
                      title="Edit member"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDeleteMember(member.user_id)}
                      data-testid={`delete-member-${member.user_id}`}
                      className="p-1 text-stark-red/50 hover:text-stark-red transition-colors"
                      title="Remove member"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {member.user_id === project?.owner_id && (
                  <span className="text-xs text-arc-cyan/50 ml-1">(Owner)</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setActiveTab("tasks")}
            data-testid="tab-tasks"
            className={`flex items-center gap-2 px-4 py-2 font-display text-sm uppercase tracking-wider transition-all ${
              activeTab === "tasks"
                ? "bg-arc-cyan/20 border border-arc-cyan text-arc-cyan"
                : "border border-muted-foreground/30 text-muted-foreground hover:border-arc-cyan/50 hover:text-arc-cyan/70"
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>Tasks ({tasks.length})</span>
          </button>
          <button
            onClick={() => setActiveTab("ideas")}
            data-testid="tab-ideas"
            className={`flex items-center gap-2 px-4 py-2 font-display text-sm uppercase tracking-wider transition-all ${
              activeTab === "ideas"
                ? "bg-jarvis-amber/20 border border-jarvis-amber text-jarvis-amber"
                : "border border-muted-foreground/30 text-muted-foreground hover:border-jarvis-amber/50 hover:text-jarvis-amber/70"
            }`}
          >
            <Lightbulb className="w-4 h-4" />
            <span>Ideas ({ideas.length})</span>
          </button>
        </div>

        {/* Ideas Section */}
        {activeTab === "ideas" && (
          <div className="glass border border-jarvis-amber/20 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-jarvis-amber" />
                <h2 className="font-display text-lg text-jarvis-amber uppercase tracking-wider">
                  Ideas
                </h2>
              </div>
            </div>
            
            {/* Add Idea Form */}
            <form onSubmit={handleAddIdea} className="space-y-3 mb-4">
              <div className="flex gap-2">
                <Input
                  value={newIdea}
                  onChange={(e) => setNewIdea(e.target.value)}
                  placeholder="Add a new idea for future tasks..."
                  data-testid="idea-input"
                  className="flex-1 bg-stark-void border-jarvis-amber/30 text-holo-white placeholder:text-muted-foreground focus:border-jarvis-amber"
                />
                <label className="flex items-center gap-1 px-3 py-2 border border-jarvis-amber/30 text-jarvis-amber cursor-pointer hover:bg-jarvis-amber/10 transition-colors">
                  <Image className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'idea')}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                </label>
                <Button
                  type="submit"
                  disabled={!newIdea.trim()}
                  data-testid="submit-idea-btn"
                  className="bg-jarvis-amber/10 border border-jarvis-amber text-jarvis-amber hover:bg-jarvis-amber hover:text-stark-void"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {newIdeaImages.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newIdeaImages.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={img} alt="" className="w-12 h-12 object-cover border border-jarvis-amber/30" />
                      <button
                        type="button"
                        onClick={() => removeImage('idea', idx)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-stark-red text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </form>
            
            {/* Ideas List */}
            {ideas.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No ideas yet. Add ideas for future tasks here.
              </p>
            ) : (
              <div className="space-y-2">
                {ideas.map((idea) => (
                  <div 
                    key={idea.idea_id} 
                    className="flex items-start gap-3 p-3 bg-jarvis-amber/5 border border-jarvis-amber/20 group"
                  >
                    <Lightbulb className="w-4 h-4 text-jarvis-amber/50 flex-shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-holo-white">{idea.content}</p>
                      {idea.images && idea.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {idea.images.map((img, idx) => (
                            <img key={idx} src={img} alt="" className="w-16 h-16 object-cover border border-jarvis-amber/30 cursor-pointer hover:opacity-80" onClick={() => window.open(img, '_blank')} />
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground font-mono mt-1">
                        by {idea.user?.name || "Unknown"} • {formatDate(idea.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setEditingIdea({ ...idea })}
                        data-testid={`edit-idea-${idea.idea_id}`}
                        className="flex items-center gap-1 px-3 py-2 text-jarvis-amber border border-jarvis-amber/50 bg-jarvis-amber/10 hover:bg-jarvis-amber/20 transition-colors text-xs font-mono uppercase"
                        title="Edit idea"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleConvertIdea(idea.idea_id)}
                        data-testid={`convert-idea-${idea.idea_id}`}
                        className="p-2 text-arc-cyan border border-arc-cyan/30 hover:bg-arc-cyan/10 transition-colors"
                        title="Convert to task"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteIdea(idea.idea_id)}
                        data-testid={`delete-idea-${idea.idea_id}`}
                        className="p-2 text-stark-red/70 border border-stark-red/30 hover:text-stark-red hover:bg-stark-red/10 transition-colors"
                        title="Delete idea"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tasks Section */}
        {activeTab === "tasks" && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-xl text-holo-white uppercase tracking-wider">
                Task Queue
              </h2>
              <div className="flex items-center gap-6">
                <p className="text-xs font-mono text-muted-foreground">
                  Drag tasks to reorder priority
                </p>
                <button
                  onClick={() => setShowTaskModal(true)}
                  data-testid="create-task-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-arc-cyan/10 border border-arc-cyan text-arc-cyan font-display text-xs uppercase tracking-widest transition-all duration-300 hover:bg-arc-cyan hover:text-stark-void glow-cyan-hover clip-chamfer"
                >
                  <Plus className="w-4 h-4" />
                  <span>New Task</span>
                </button>
              </div>
            </div>

        {/* Tasks Grid */}
        {tasks.length === 0 ? (
          <div className="glass border border-arc-cyan/20 p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-arc-cyan/30 mx-auto mb-4" />
            <p className="font-display text-lg text-holo-white uppercase tracking-wider mb-2">
              No Tasks
            </p>
            <p className="text-muted-foreground text-sm mb-6">
              Create your first task to start tracking progress
            </p>
            <button
              onClick={() => setShowTaskModal(true)}
              data-testid="create-first-task-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-arc-cyan/10 border border-arc-cyan text-arc-cyan font-display text-xs uppercase tracking-widest transition-all duration-300 hover:bg-arc-cyan hover:text-stark-void"
            >
              <Plus className="w-4 h-4" />
              <span>Create Task</span>
            </button>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="tasks">
              {(provided) => (
                <div 
                  className="grid gap-4"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {sortedTasks.map((task, index) => (
                    <Draggable 
                      key={task.task_id} 
                      draggableId={task.task_id} 
                      index={index}
                      isDragDisabled={task.status === "completed"}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={snapshot.isDragging ? "opacity-90" : ""}
                        >
                          <TaskCard
                            task={task}
                            members={members}
                            getMemberName={getMemberName}
                            getPriorityColor={getPriorityColor}
                            getPriorityLabel={getPriorityLabel}
                            getPriorityText={getPriorityText}
                            formatDate={formatDate}
                            formatDueDate={formatDueDate}
                            isOverdue={isOverdue}
                            isDueSoon={isDueSoon}
                            onStatusToggle={() => handleUpdateTask(task.task_id, { 
                              status: task.status === "completed" ? "pending" : "completed" 
                            })}
                            onClick={() => openTaskDetail(task)}
                            onDelete={() => handleDeleteTask(task.task_id)}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
          </>
        )}
      </main>

      {/* Create Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="bg-stark-carbon border border-arc-cyan/30 text-holo-white max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wider text-arc-cyan">
              Create Task
            </DialogTitle>
            <p className="sr-only">Create a new task with title, description, priority, and assignee</p>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4 mt-4 overflow-y-auto flex-1 pr-2">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                Task Title
              </Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Enter task title"
                data-testid="task-title-input"
                className="bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                Description
              </Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Task details"
                data-testid="task-description-input"
                className="bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan min-h-[80px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                  Priority (1-10)
                </Label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({ ...newTask, priority: parseInt(e.target.value) })}
                  data-testid="task-priority-select"
                  className="w-full bg-stark-void border border-arc-cyan/30 text-holo-white px-3 py-2 focus:border-arc-cyan outline-none"
                >
                  {[1,2,3,4,5,6,7,8,9,10].map((num) => (
                    <option key={num} value={num}>
                      Position {num} {usedPriorities.includes(num) ? '• taken' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Lower number = higher priority. Selecting a taken position will swap.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                  Assign To
                </Label>
                <select
                  value={newTask.assigned_to}
                  onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })}
                  data-testid="task-assignee-select"
                  className="w-full bg-stark-void border border-arc-cyan/30 text-holo-white px-3 py-2 focus:border-arc-cyan outline-none"
                >
                  <option value="">Unassigned</option>
                  {members.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Due Date Picker */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                Due Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="task-due-date-picker"
                    className={`w-full justify-start text-left font-normal bg-stark-void border-arc-cyan/30 text-holo-white hover:bg-arc-cyan/10 ${!newTask.due_date && "text-muted-foreground"}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newTask.due_date ? format(newTask.due_date, "PPP") : "Select due date (optional)"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-stark-carbon border-arc-cyan/30" align="start">
                  <Calendar
                    mode="single"
                    selected={newTask.due_date}
                    onSelect={(date) => setNewTask({ ...newTask, due_date: date })}
                    initialFocus
                    className="bg-stark-carbon text-holo-white"
                  />
                  {newTask.due_date && (
                    <div className="p-2 border-t border-arc-cyan/20">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setNewTask({ ...newTask, due_date: null })}
                        className="w-full text-muted-foreground hover:text-stark-red"
                      >
                        Clear due date
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Image Upload for Task */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                Images
              </Label>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 px-3 py-2 border border-arc-cyan/30 text-arc-cyan text-sm cursor-pointer hover:bg-arc-cyan/10 transition-colors">
                  <Upload className="w-4 h-4" />
                  <span>{uploadingImage ? "Uploading..." : "Upload Image"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'task')}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                </label>
              </div>
              {newTask.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newTask.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={img} alt="" className="w-16 h-16 object-cover border border-arc-cyan/30" />
                      <button
                        type="button"
                        onClick={() => removeImage('task', idx)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-stark-red text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowTaskModal(false)}
                className="flex-1 border-muted-foreground/30 text-muted-foreground hover:bg-muted/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-task-btn"
                className="flex-1 bg-arc-cyan/10 border border-arc-cyan text-arc-cyan hover:bg-arc-cyan hover:text-stark-void font-display uppercase tracking-wider"
              >
                {submitting ? "Creating..." : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Member Modal */}
      <Dialog open={showMemberModal} onOpenChange={setShowMemberModal}>
        <DialogContent className="bg-stark-carbon border border-arc-cyan/30 text-holo-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wider text-arc-cyan">
              Add Team Member
            </DialogTitle>
            <p className="sr-only">Add a team member by name</p>
          </DialogHeader>
          <form onSubmit={handleAddMember} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                Name *
              </Label>
              <Input
                type="text"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                placeholder="Enter team member name"
                data-testid="member-name-input"
                className="bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                Email (Optional)
              </Label>
              <Input
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="team.member@example.com"
                data-testid="member-email-input"
                className="bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan"
              />
              <p className="text-xs text-muted-foreground">
                If email matches an existing user, they will be linked automatically
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowMemberModal(false)}
                className="flex-1 border-muted-foreground/30 text-muted-foreground hover:bg-muted/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-member-btn"
                className="flex-1 bg-arc-cyan/10 border border-arc-cyan text-arc-cyan hover:bg-arc-cyan hover:text-stark-void font-display uppercase tracking-wider"
              >
                {submitting ? "Adding..." : "Add Member"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Member Modal */}
      <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
        <DialogContent className="bg-stark-carbon border border-arc-cyan/30 text-holo-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wider text-arc-cyan">
              Edit Team Member
            </DialogTitle>
            <p className="sr-only">Edit team member name</p>
          </DialogHeader>
          {editingMember && (
            <form onSubmit={handleUpdateMember} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                  Name
                </Label>
                <Input
                  type="text"
                  value={editingMember.name}
                  onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                  placeholder="Enter team member name"
                  data-testid="edit-member-name-input"
                  className="bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan"
                />
              </div>
              {editingMember.email && (
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                    Email
                  </Label>
                  <p className="text-sm text-muted-foreground font-mono">{editingMember.email}</p>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingMember(null)}
                  className="flex-1 border-muted-foreground/30 text-muted-foreground hover:bg-muted/10"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !editingMember.name?.trim()}
                  data-testid="submit-edit-member-btn"
                  className="flex-1 bg-arc-cyan/10 border border-arc-cyan text-arc-cyan hover:bg-arc-cyan hover:text-stark-void font-display uppercase tracking-wider"
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      <Dialog open={!!showTaskDetail} onOpenChange={() => setShowTaskDetail(null)}>
        <DialogContent className="bg-stark-carbon border border-arc-cyan/30 text-holo-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {showTaskDetail && (
            <>
              <DialogHeader className="flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className={`inline-block px-2 py-1 text-xs font-mono uppercase tracking-wider mb-2 ${getPriorityLabel(showTaskDetail.priority)} border ${parseInt(showTaskDetail.priority) <= 3 || showTaskDetail.priority === 'high' ? 'border-stark-red/50 bg-stark-red/10' : parseInt(showTaskDetail.priority) <= 6 || showTaskDetail.priority === 'medium' ? 'border-jarvis-amber/50 bg-jarvis-amber/10' : 'border-arc-cyan/50 bg-arc-cyan/10'}`}>
                      {typeof showTaskDetail.priority === 'number' || !isNaN(parseInt(showTaskDetail.priority)) 
                        ? `Priority ${showTaskDetail.priority} - ${getPriorityText(showTaskDetail.priority)}`
                        : `${showTaskDetail.priority} priority`
                      }
                    </div>
                    <DialogTitle className="font-display text-xl uppercase tracking-wider text-holo-white">
                      {showTaskDetail.title}
                    </DialogTitle>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-6 py-4">
                {/* Status & Assignment */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                      Position
                    </Label>
                    <select
                      value={showTaskDetail.priority}
                      onChange={(e) => handleUpdateTask(showTaskDetail.task_id, { priority: parseInt(e.target.value) })}
                      data-testid="update-priority-select"
                      className="w-full bg-stark-void border border-arc-cyan/30 text-holo-white px-3 py-2 focus:border-arc-cyan outline-none"
                    >
                      {[1,2,3,4,5,6,7,8,9,10].map((num) => {
                        const currentPriority = typeof showTaskDetail.priority === 'number' ? showTaskDetail.priority : parseInt(showTaskDetail.priority);
                        const isTaken = usedPriorities.includes(num) && num !== currentPriority;
                        return (
                          <option key={num} value={num}>
                            Position {num} {isTaken ? '• swap' : ''}
                          </option>
                        );
                      })}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Lower number = higher priority. Selecting a taken position will swap.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                      Assigned To
                    </Label>
                    <select
                      value={showTaskDetail.assigned_to || ""}
                      onChange={(e) => handleUpdateTask(showTaskDetail.task_id, { assigned_to: e.target.value || null })}
                      data-testid="update-assignee-select"
                      className="w-full bg-stark-void border border-arc-cyan/30 text-holo-white px-3 py-2 focus:border-arc-cyan outline-none"
                    >
                      <option value="">Unassigned</option>
                      {members.map((member) => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                      Status
                    </Label>
                    <button
                      onClick={() => handleUpdateTask(showTaskDetail.task_id, { 
                        status: showTaskDetail.status === "completed" ? "pending" : "completed" 
                      })}
                      data-testid="toggle-task-status"
                      className={`flex items-center gap-2 px-3 py-2 border transition-colors w-full ${
                        showTaskDetail.status === "completed"
                          ? "border-green-500/50 bg-green-500/10 text-green-400"
                          : "border-jarvis-amber/50 bg-jarvis-amber/10 text-jarvis-amber"
                      }`}
                    >
                      {showTaskDetail.status === "completed" ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-sm font-mono uppercase">Completed</span>
                      </>
                    ) : (
                      <>
                        <Circle className="w-4 h-4" />
                        <span className="text-sm font-mono uppercase">Pending</span>
                      </>
                    )}
                  </button>
                  </div>

                  {/* Due Date */}
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                      Due Date
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="update-due-date-picker"
                          className={`w-full justify-start text-left font-normal bg-stark-void border-arc-cyan/30 hover:bg-arc-cyan/10 ${
                            isOverdue(showTaskDetail.due_date, showTaskDetail.status) 
                              ? "text-stark-red border-stark-red/50" 
                              : isDueSoon(showTaskDetail.due_date, showTaskDetail.status)
                                ? "text-jarvis-amber border-jarvis-amber/50"
                                : "text-holo-white"
                          } ${!showTaskDetail.due_date && "text-muted-foreground"}`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {showTaskDetail.due_date ? (
                            <>
                              {formatDueDate(showTaskDetail.due_date)}
                              {isOverdue(showTaskDetail.due_date, showTaskDetail.status) && (
                                <span className="ml-2 text-xs">(OVERDUE)</span>
                              )}
                              {isDueSoon(showTaskDetail.due_date, showTaskDetail.status) && !isOverdue(showTaskDetail.due_date, showTaskDetail.status) && (
                                <span className="ml-2 text-xs">(Due soon)</span>
                              )}
                            </>
                          ) : "No due date set"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-stark-carbon border-arc-cyan/30" align="start">
                        <Calendar
                          mode="single"
                          selected={showTaskDetail.due_date ? new Date(showTaskDetail.due_date) : undefined}
                          onSelect={(date) => handleUpdateTask(showTaskDetail.task_id, { 
                            due_date: date ? date.toISOString().split('T')[0] : null 
                          })}
                          initialFocus
                          className="bg-stark-carbon text-holo-white"
                        />
                        {showTaskDetail.due_date && (
                          <div className="p-2 border-t border-arc-cyan/20">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateTask(showTaskDetail.task_id, { due_date: null })}
                              className="w-full text-muted-foreground hover:text-stark-red"
                            >
                              Clear due date
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                      Description
                    </Label>
                    {!editingDescription && (
                      <button
                        onClick={() => {
                          setNewDescription(showTaskDetail.description || "");
                          setEditingDescription(true);
                        }}
                        className="p-1 text-arc-cyan/50 hover:text-arc-cyan transition-colors"
                        data-testid="edit-description-btn"
                        title="Edit description"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {editingDescription ? (
                    <div className="space-y-2">
                      <Textarea
                        value={newDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                        placeholder="Add a description..."
                        data-testid="edit-description-input"
                        className="bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan min-h-[80px]"
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleUpdateDescription}
                          data-testid="save-description-btn"
                          className="bg-arc-cyan/10 border border-arc-cyan text-arc-cyan hover:bg-arc-cyan hover:text-stark-void text-xs"
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingDescription(false)}
                          className="text-muted-foreground hover:text-holo-white text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {showTaskDetail.description || <span className="italic text-muted-foreground/50">No description</span>}
                    </p>
                  )}
                </div>

                {/* Images */}
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                    Images
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {showTaskDetail.images && showTaskDetail.images.map((img, idx) => (
                      <img 
                        key={idx} 
                        src={img} 
                        alt="" 
                        className="w-20 h-20 object-cover border border-arc-cyan/30 cursor-pointer hover:opacity-80" 
                        onClick={() => window.open(img, '_blank')} 
                      />
                    ))}
                    <label className="w-20 h-20 border border-dashed border-arc-cyan/30 flex items-center justify-center cursor-pointer hover:bg-arc-cyan/10 transition-colors">
                      <div className="text-center">
                        <Upload className="w-5 h-5 text-arc-cyan/50 mx-auto" />
                        <span className="text-xs text-arc-cyan/50">Add</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'taskDetail')}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-4 text-xs font-mono">
                  <div>
                    <span className="text-arc-cyan/50 uppercase">Created:</span>
                    <span className="text-muted-foreground ml-2">{formatDate(showTaskDetail.created_at)}</span>
                  </div>
                  {showTaskDetail.completed_at && (
                    <div>
                      <span className="text-arc-cyan/50 uppercase">Completed:</span>
                      <span className="text-muted-foreground ml-2">{formatDate(showTaskDetail.completed_at)}</span>
                    </div>
                  )}
                </div>

                {/* Comments Section */}
                <div className="space-y-4 border-t border-arc-cyan/10 pt-4">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-arc-cyan" />
                    <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                      Comments ({comments.length})
                    </Label>
                  </div>
                  
                  {comments.length > 0 && (
                    <ScrollArea className="max-h-[200px]">
                      <div className="space-y-3">
                        {comments.map((comment) => (
                          <div key={comment.comment_id} className="glass border border-arc-cyan/10 p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Avatar className="w-6 h-6">
                                <AvatarImage src={comment.user?.picture} />
                                <AvatarFallback className="bg-arc-cyan/20 text-arc-cyan text-xs">
                                  {comment.user?.name?.charAt(0) || "U"}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm text-holo-white font-medium">
                                {comment.user?.name || "Unknown"}
                              </span>
                              <span className="text-xs text-muted-foreground font-mono">
                                {formatDate(comment.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground pl-8">
                              {comment.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                  
                  {/* Add Comment Form */}
                  <form onSubmit={handleAddComment} className="flex gap-2">
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      data-testid="comment-input"
                      className="flex-1 bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan"
                    />
                    <Button
                      type="submit"
                      disabled={!newComment.trim()}
                      data-testid="submit-comment-btn"
                      className="bg-arc-cyan/10 border border-arc-cyan text-arc-cyan hover:bg-arc-cyan hover:text-stark-void"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </div>

                {/* Delete Task Button */}
                <div className="border-t border-arc-cyan/10 pt-4 flex items-center gap-3">
                  <button
                    onClick={() => handleConvertTaskToIdea(showTaskDetail.task_id)}
                    data-testid="convert-to-idea-btn"
                    className="flex items-center gap-2 px-4 py-2 border border-jarvis-amber/50 text-jarvis-amber hover:bg-jarvis-amber/10 transition-colors text-sm font-mono uppercase tracking-wider"
                  >
                    <Lightbulb className="w-4 h-4" />
                    <span>Move to Ideas</span>
                  </button>
                  <button
                    onClick={() => handleDeleteTask(showTaskDetail.task_id)}
                    data-testid="delete-task-btn"
                    className="flex items-center gap-2 px-4 py-2 border border-stark-red/50 text-stark-red hover:bg-stark-red/10 transition-colors text-sm font-mono uppercase tracking-wider"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Task</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Idea Modal */}
      <Dialog open={!!editingIdea} onOpenChange={() => setEditingIdea(null)}>
        <DialogContent className="bg-stark-carbon border border-jarvis-amber/30 text-holo-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wider text-jarvis-amber">
              Edit Idea
            </DialogTitle>
            <p className="sr-only">Edit idea content and images</p>
          </DialogHeader>
          {editingIdea && (
            <form onSubmit={handleUpdateIdea} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-jarvis-amber/80">
                  Idea Content
                </Label>
                <Textarea
                  value={editingIdea.content}
                  onChange={(e) => setEditingIdea({ ...editingIdea, content: e.target.value })}
                  placeholder="Describe your idea..."
                  data-testid="edit-idea-content-input"
                  className="bg-stark-void border-jarvis-amber/30 text-holo-white placeholder:text-muted-foreground focus:border-jarvis-amber min-h-[100px]"
                />
              </div>
              
              {/* Image Upload for Editing Idea */}
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-jarvis-amber/80">
                  Images
                </Label>
                <div className="flex flex-wrap gap-2">
                  {editingIdea.images && editingIdea.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={img} 
                        alt="" 
                        className="w-16 h-16 object-cover border border-jarvis-amber/30 cursor-pointer hover:opacity-80" 
                        onClick={() => window.open(img, '_blank')} 
                      />
                      <button
                        type="button"
                        onClick={() => removeEditIdeaImage(idx)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-stark-red text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <label className="w-16 h-16 border border-dashed border-jarvis-amber/30 flex items-center justify-center cursor-pointer hover:bg-jarvis-amber/10 transition-colors">
                    <div className="text-center">
                      <Upload className="w-4 h-4 text-jarvis-amber/50 mx-auto" />
                      <span className="text-xs text-jarvis-amber/50">Add</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditIdeaImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingIdea(null)}
                  className="flex-1 border-muted-foreground/30 text-muted-foreground hover:bg-muted/10"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !editingIdea.content?.trim()}
                  data-testid="submit-edit-idea-btn"
                  className="flex-1 bg-jarvis-amber/10 border border-jarvis-amber text-jarvis-amber hover:bg-jarvis-amber hover:text-stark-void font-display uppercase tracking-wider"
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TaskCard = ({ task, members, getMemberName, getPriorityColor, getPriorityLabel, getPriorityText, formatDate, formatDueDate, isOverdue, isDueSoon, onStatusToggle, onClick, onDelete, dragHandleProps }) => {
  const assignee = members.find(m => m.user_id === task.assigned_to);
  const priorityNum = typeof task.priority === 'number' ? task.priority : parseInt(task.priority);
  const isNumericPriority = !isNaN(priorityNum);
  const taskIsOverdue = isOverdue(task.due_date, task.status);
  const taskIsDueSoon = isDueSoon(task.due_date, task.status);
  
  return (
    <div
      className={`glass border border-arc-cyan/20 p-4 task-card ${
        task.status === "completed" ? "opacity-60" : ""
      } ${taskIsOverdue ? "border-stark-red/50" : ""}`}
      data-testid={`task-card-${task.task_id}`}
    >
      <div className="flex items-start gap-4">
        {/* Drag Handle */}
        {task.status !== "completed" && dragHandleProps && (
          <div 
            {...dragHandleProps}
            className="flex-shrink-0 cursor-grab active:cursor-grabbing mt-3 text-muted-foreground hover:text-arc-cyan transition-colors"
            title="Drag to reorder"
          >
            <GripVertical className="w-5 h-5" />
          </div>
        )}
        
        {/* Priority Number Badge */}
        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border border-arc-cyan/50 bg-arc-cyan/10 text-arc-cyan">
          <span className="font-display text-lg font-bold">
            {isNumericPriority ? priorityNum : task.priority?.charAt(0)?.toUpperCase()}
          </span>
        </div>

        {/* Status Toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onStatusToggle(); }}
          data-testid={`task-status-toggle-${task.task_id}`}
          className="mt-2 flex-shrink-0"
        >
          {task.status === "completed" ? (
            <CheckCircle2 className="w-5 h-5 text-green-400" />
          ) : (
            <Circle className="w-5 h-5 text-muted-foreground hover:text-arc-cyan transition-colors" />
          )}
        </button>

        {/* Task Content */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
          <h3 className={`font-display text-sm uppercase tracking-wider mb-1 ${
            task.status === "completed" ? "line-through text-muted-foreground" : "text-holo-white"
          }`}>
            {task.title}
          </h3>
          
          {task.description && (
            <p className="text-xs text-muted-foreground line-clamp-1 mb-2">
              {task.description}
            </p>
          )}

          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs font-mono text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{formatDate(task.created_at)}</span>
            </div>
            {task.due_date && (
              <div className={`flex items-center gap-1 ${
                taskIsOverdue ? "text-stark-red" : taskIsDueSoon ? "text-jarvis-amber" : ""
              }`}>
                <CalendarIcon className="w-3 h-3" />
                <span>Due: {formatDueDate(task.due_date)}</span>
                {taskIsOverdue && <AlertTriangle className="w-3 h-3" />}
              </div>
            )}
            {assignee && (
              <div className="flex items-center gap-1">
                <Avatar className="w-4 h-4">
                  <AvatarImage src={assignee.picture} />
                  <AvatarFallback className="bg-arc-cyan/20 text-arc-cyan text-[8px]">
                    {assignee.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <span>{assignee.name}</span>
              </div>
            )}
            {task.status === "completed" && task.completed_at && (
              <span className="text-green-400">
                Completed {formatDate(task.completed_at)}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 text-muted-foreground hover:text-holo-white transition-colors"
              data-testid={`task-menu-${task.task_id}`}
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-stark-carbon border-arc-cyan/30">
            <DropdownMenuItem
              onClick={onClick}
              className="text-holo-white hover:bg-arc-cyan/10 cursor-pointer"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-stark-red hover:bg-stark-red/10 cursor-pointer"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default ProjectDetail;
